import React, { useEffect, useState } from 'react';
import { Button, Collapse, DatePicker, Input, Modal, Select, Space, Tag, Timeline, message } from 'antd';
import dayjs from 'libs/dayjs';
import { healthStyle } from 'pages/liftlogic/v2/Components/healthStyle';
import {
  getComponentHealthLog,
  setComponentAck,
  setComponentOverride,
} from 'api/componentHealth';

const STATE_OPTIONS = ['OPERATIVO', 'OBSERVACION', 'CRITICO'].map((s) => ({
  value: s,
  label: healthStyle(s).label,
}));

function ComponentHealthPanel({ equipoId, nodeId, health, onChanged }) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideState, setOverrideState] = useState('OPERATIVO');
  const [overrideNote, setOverrideNote] = useState('');
  const [overrideExpires, setOverrideExpires] = useState(null);
  const [overrideLoading, setOverrideLoading] = useState(false);

  const [logLoaded, setLogLoaded] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // FIX I-2: reset historial cuando cambia el nodo seleccionado
  useEffect(() => {
    setLogLoaded(false);
    setLogItems([]);
  }, [equipoId, nodeId]);

  if (!health) return null;

  const hs = healthStyle(health.effective);

  // ---- ACK handlers ----
  const handleAck = async (on) => {
    try {
      await setComponentAck(equipoId, nodeId, on);
      onChanged?.();
    } catch {
      message.error('No se pudo actualizar el seguimiento');
    }
  };

  // ---- Override handlers ----
  const openOverride = () => {
    setOverrideState('OPERATIVO');
    setOverrideNote('');
    setOverrideExpires(null);
    setOverrideOpen(true);
  };

  const handleOverrideOk = async () => {
    if (!overrideNote.trim()) return;
    setOverrideLoading(true);
    try {
      await setComponentOverride(equipoId, nodeId, {
        state: overrideState,
        note: overrideNote.trim(),
        expires_at: overrideExpires ? overrideExpires.toISOString() : undefined,
      });
      onChanged?.();
      setOverrideOpen(false);
    } catch {
      message.error('No se pudo forzar el estado');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleRemoveOverride = async () => {
    try {
      await setComponentOverride(equipoId, nodeId, { state: null });
      onChanged?.();
    } catch {
      message.error('No se pudo quitar el forzado');
    }
  };

  // ---- Log (lazy load) ----
  const loadLog = async () => {
    if (logLoaded) return;
    setLogLoading(true);
    try {
      const d = await getComponentHealthLog(equipoId, nodeId);
      setLogItems(d?.log || []);
      setLogLoaded(true);
    } catch {
      message.error('No se pudo cargar el historial');
    } finally {
      setLogLoading(false);
    }
  };

  const logCollapseItems = [
    {
      key: 'log',
      label: 'Historial de salud',
      children: logLoading ? (
        <div style={{ color: '#9ca3af', fontSize: 12 }}>Cargando…</div>
      ) : logItems.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: 12 }}>Sin registros.</div>
      ) : (
        <Timeline
          mode="left"
          items={logItems.map((entry, idx) => ({
            key: entry.id ?? `${entry.createdAt}-${entry.action}-${idx}`,
            label: dayjs(entry.createdAt).format('DD/MM/YY HH:mm'),
            children: (
              <div>
                <Space size={4} wrap>
                  <Tag style={{ fontSize: 11 }}>{entry.action}</Tag>
                  {entry.to_state && (
                    <Tag color={healthStyle(entry.to_state).color} style={{ fontSize: 11 }}>
                      {healthStyle(entry.to_state).label}
                    </Tag>
                  )}
                  {entry.actor && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{entry.actor}</span>
                  )}
                </Space>
                {entry.note && (
                  <div style={{ fontSize: 12, marginTop: 2, color: '#374151' }}>{entry.note}</div>
                )}
              </div>
            ),
          }))}
        />
      ),
    },
  ];

  return (
    <div
      style={{
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        padding: '10px 12px',
        marginBottom: 14,
        background: '#fafafa',
      }}
    >
      {/* Badge de estado efectivo */}
      <Space size={8} wrap style={{ marginBottom: 6 }}>
        <Tag color={hs.color} style={{ fontWeight: 600 }}>
          {hs.label}
        </Tag>
        {health.override && (
          <span style={{ fontSize: 12, color: '#b8861a' }}>
            ⚠ Forzado por {health.override.by}
          </span>
        )}
        {health.ack && !health.override && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            👁 En seguimiento por {health.ack.by}
          </span>
        )}
      </Space>

      {/* Controles */}
      <Space size={6} wrap>
        {/* Quitar override */}
        {health.override && (
          <Button size="small" danger onClick={handleRemoveOverride}>
            Quitar
          </Button>
        )}

        {/* ACK / quitar ACK */}
        {!health.ack ? (
          <Button size="small" onClick={() => handleAck(true)}>
            Marcar en seguimiento
          </Button>
        ) : (
          <Button size="small" onClick={() => handleAck(false)}>
            Quitar seguimiento
          </Button>
        )}

        {/* Override */}
        <Button size="small" onClick={openOverride}>
          Forzar estado…
        </Button>
      </Space>

      {/* Historial */}
      <Collapse
        ghost
        size="small"
        style={{ marginTop: 8 }}
        items={logCollapseItems}
        onChange={(keys) => {
          if (keys.includes('log')) loadLog();
        }}
      />

      {/* Modal de override */}
      <Modal
        title="Forzar estado del componente"
        open={overrideOpen}
        onCancel={() => {
          setOverrideOpen(false);
          // FIX I-1: limpiar campos al cerrar para que no queden sucios al reabrir
          setOverrideState('OPERATIVO');
          setOverrideNote('');
          setOverrideExpires(null);
        }}
        onOk={handleOverrideOk}
        okButtonProps={{ disabled: !overrideNote.trim(), loading: overrideLoading }}
        okText="OK"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4, color: '#374151' }}>Estado</div>
            <Select
              value={overrideState}
              onChange={setOverrideState}
              options={STATE_OPTIONS}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4, color: '#374151' }}>
              Nota <span style={{ color: '#e02b2b' }}>*</span>
            </div>
            <Input.TextArea
              rows={3}
              placeholder="Motivo del forzado (obligatorio)"
              value={overrideNote}
              onChange={(e) => setOverrideNote(e.target.value)}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4, color: '#374151' }}>
              Expira (opcional)
            </div>
            <DatePicker
              value={overrideExpires}
              onChange={setOverrideExpires}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              disabledDate={(d) => d && d.isBefore(dayjs(), 'day')}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}

export default ComponentHealthPanel;
